import React, { Fragment, useEffect, useState, useCallback } from 'react'
import Button from '@mui/material/Button'
import DatePicker from '@mui/lab/DatePicker';
import { GetAgreements, AddAgreement, GetAgreementById, AddDocument, AddRemark, UpdateAgreement, createAgreementInvoice } from '../../REST/agreements'
import { GetActiveCompanies } from '../../REST/company'
import { GetInvoiceCycles, GetPaymentTypes } from '../../REST/utilities'
import {
    DataGrid, GridToolbarContainer,
    GridToolbarExport,
    gridClasses,
} from '@mui/x-data-grid';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom'
import AuthProvider from '../Authentication/authProvider'
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { useForm, Controller, useFormState, useWatch } from "react-hook-form";
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import DocumentView from '../Utilities/documents';
import RemarksView from '../Utilities/remarks';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import NativeSelect from '@mui/material/NativeSelect';
import FormHelperText from '@mui/material/FormHelperText';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import PropertyList from './property';
import Access from '../../Utils/authorize';
import { dateFormat } from '../../helpers/dates';
import AddIcon from '@mui/icons-material/Add';
import { handleErr } from '../../helpers/errors';
import { dateCompare } from '../../helpers/dates';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import ResponsiveDataGrid from '../../themes/components/ResponsiveDataGrid';
import Form from '../../themes/components/Form';
import FormSection from '../../themes/components/FormSection';
import FormActions from '../../themes/components/FormActions';
import LocalSubnav from '../../themes/components/LocalSubnav';
import ResponsiveDialog from '../../themes/components/ResponsiveDialog';
import SaveIcon from '@mui/icons-material/Save';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import NoteIcon from '@mui/icons-material/Note';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MenuIcon from '@mui/icons-material/Menu';
import EmailIcon from '@mui/icons-material/Email';
import { useTheme, useMediaQuery } from '@mui/material';
import SendTemplateDialog from '../../themes/components/SendTemplateDialog';

function CustomToolbar() {
    return (
        <GridToolbarContainer className={gridClasses.toolbarContainer}>
            <GridToolbarExport />
        </GridToolbarContainer>
    );
}

function AgreementList() {

    const navigate = useNavigate()

    const dateParser = (params) => dateFormat(params.value)

    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.5, hide: true },
        { field: 'title', headerName: 'Account', flex: 0.5, minVisible: 'xs' },
        { field: 'agreementNum', headerName: 'Account Number', flex: 0.5, minVisible: 'xs' },
        { field: 'startDate', headerName: 'Begin Date', flex: 0.5, minVisible: 'md', type: 'date', renderCell: dateParser, sortComparator: (v1, v2) => dateCompare(v1, v2) },
        { field: 'endDate', headerName: 'End Date', flex: 0.5, minVisible: 'md', type: 'date', renderCell: dateParser, sortComparator: (v1, v2) => dateCompare(v1, v2) },
        { field: 'status', headerName: 'Status', flex: 0.5, minVisible: 'sm', type: 'singleSelect', valueOptions: ['Active', 'Archived', 'Expiring Soon', 'Expired'] },
    ];

    const fetchAgreements = useCallback(async (page, limit, filters, sort) => {
        const params = {
            page,
            limit
        }
        
        if (Object.keys(filters).length > 0) {
            params.filters = JSON.stringify(filters)
        }
        
        if (sort) {
            params.sort = JSON.stringify(sort)
        }
        
        const result = await GetAgreements(params);

        if (result.data?.data) {
            const transformed = result.data.data.map(e => ({
                ...e,
                id: e._id
            }));
            return {
                data: transformed,
                pagination: result.data.pagination
            };
        }

        if (Array.isArray(result.data)) {
            const transformed = result.data.map(e => ({ ...e, id: e._id }));
            return {
                data: transformed,
                pagination: { total: transformed.length, page: 1, totalPages: 1 }
            };
        }

        return { data: [], pagination: { total: 0, page: 1, totalPages: 0 } };
    }, []);

    const handleRowClick = (params) => {
        navigate('/agreements/' + params.id);
    };

    return (
        <PageShell>
            <PageHeader
                title="Accounts"
                back
                onBack={() => navigate('/')}
                actions={
                    Access.isAdmin('Lease') ? (
                        <Action
                            primary
                            icon={<AddIcon />}
                            label="Add Account"
                            onClick={() => navigate('/agreements/add')}
                        />
                    ) : null
                }
            />

            <PageContent fill>
                <ResponsiveDataGrid
                    columns={columns}
                    fetchData={fetchAgreements}
                    initialPageSize={10}
                    pageSizeOptions={[10, 25, 50, 100]}
                    onRowClick={handleRowClick}
                    clientSort={{ field: 'title', order: 'asc' }}
                />
            </PageContent>
        </PageShell>
    );
}

export default function AgreementView() {
    const location = useLocation()

    return <Fragment>
        {location.pathname === '/agreements' && <AgreementList />}
        <Routes>
            <Route path='add' element={<AuthProvider><AddAgreementView /></AuthProvider>} />
            <Route path=':id' element={<AuthProvider><EditAgreementView /></AuthProvider>} />
        </Routes>
    </Fragment>
}

const agreementSchema = yup.object({
    title: yup.string().trim().required(),
    agreementNum: yup.string().trim().required(),
    description: yup.string().trim(),
    startDate: yup.date().nullable().required(),
    endDate: yup.date().nullable().when('renewable', {
        is: true,
        then: schema => schema.required('End Date is required'),
        otherwise: schema => schema.nullable().notRequired()
    }),
    status: yup.string().trim().required(),
    renewable: yup.boolean().required(),
    tenantId: yup.string().trim().required(),
    invoiceCycleId: yup.string().trim().required(),
    rentAdjustmentType: yup.string().trim().required(),
    leaseTerms: yup.string().trim().notRequired(),
    adjustmentFreq: yup
        .number()
        .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? null : value
        )
        .nullable()
        .notRequired(),
    adjustmentDate: yup.date().nullable().notRequired(),
    fundCode: yup.string().trim(),
    orgCode: yup.string().trim(),
    objectCode: yup.string().trim(),
    gasb87Compliant: yup.boolean(),
});

const agrrementSchemaNoEnd = yup.object({
    title: yup.string().trim().required(),
    agreementNum: yup.string().trim().required(),
    description: yup.string().trim(),
    startDate: yup.string().trim().required(),
    endDate: yup.string().trim(),
    status: yup.string().trim().required(),
    renewable: yup.boolean().required(),
    tenantId: yup.string().trim().required(),
    invoiceCycleId: yup.string().trim().required(),
    rentAdjustmentType: yup.string().trim().required(),
    fundCode: yup.string().trim(),
    orgCode: yup.string().trim(),
    objectCode: yup.string().trim(),
    gasb87Compliant: yup.boolean(),
});

const AGREEMENT_FORM_DEFAULTS = {
    title: '',
    agreementNum: '',
    description: '',
    startDate: null,
    endDate: null,
    status: '',
    tenantId: '',
    renewable: false,
    leaseTerms: '',
    invoiceCycleId: '',
    rentAdjustmentType: '',
    adjustmentDate: null,
    adjustmentFreq: '',
    fundCode: '',
    orgCode: '',
    objectCode: '',
    gasb87Compliant: false
};

const toAgreementFormValues = (data = {}) => ({
    ...AGREEMENT_FORM_DEFAULTS,
    ...data,

    title: data.title ?? '',
    agreementNum: data.agreementNum ?? '',
    description: data.description ?? '',
    status: data.status ?? '',
    tenantId: data.tenantId ?? '',
    renewable: Boolean(data.renewable),
    leaseTerms: data.leaseTerms ?? '',
    invoiceCycleId: data.invoiceCycleId ?? '',
    rentAdjustmentType: data.rentAdjustmentType ?? '',

    startDate: data.startDate ? new Date(data.startDate) : null,
    endDate: data.endDate ? new Date(data.endDate) : null,
    adjustmentDate: data.adjustmentDate ? new Date(data.adjustmentDate) : null,

    adjustmentFreq: data.adjustmentFreq == null ? '' : String(data.adjustmentFreq),
    fundCode: data.fundCode ?? '',
    orgCode: data.orgCode ?? '',
    objectCode: data.objectCode ?? '',
    gasb87Compliant: Boolean(data.gasb87Compliant)
});

const toAgreementPayload = (values) => ({
    ...values,
    renewable: Boolean(values.renewable),
    endDate: values.renewable ? values.endDate : null,
    adjustmentDate: values.adjustmentDate || null,
    adjustmentFreq:
        values.adjustmentFreq === '' || values.adjustmentFreq == null
            ? null
            : Number(values.adjustmentFreq),
    gasb87Compliant: Boolean(values.gasb87Compliant)
});
export const getOptions = (opts = []) => {
    return [<option key='temp'>Select One</option>].concat(opts.map((e, idx) => {
        if (typeof e === 'string')
            return <option key={idx} value={e}>{e}</option>
        return <option key={idx} value={e._id}>{e.name}</option>
    }))
}

function AgreementFields({
    control,
    errors,
    menu,  
    disabled = false,
}) {
    const renewable = useWatch(
        {
            control,
            name: 'renewable'
        }
    );
    const isError = (val) => val !== undefined && val !== null && typeof val === 'object'

    return (
        <>
            <FormSection title="Account Details">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Controller name="title" control={control} render={({ field }) => <TextField size='small' margin='dense' required disabled={disabled} error={isError(errors.title)} helperText={errors.title && errors.title.message} fullWidth label='Account' {...field} />} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="agreementNum" control={control} render={({ field }) => <TextField size='small' margin='dense' required disabled={disabled} error={isError(errors.agreementNum)} helperText={errors.agreementNum && errors.agreementNum.message} fullWidth label='Account Number' {...field} />} />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller name="description" control={control} render={({ field }) => <TextField size='small' margin='dense' required disabled={disabled} error={isError(errors.description)} helperText={errors.description && errors.description.message} fullWidth label='Description' {...field} />} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Controller name="fundCode" control={control} render={({ field }) => <TextField size='small' margin='dense' disabled={disabled} error={isError(errors.fundCode)} helperText={errors.fundCode && errors.fundCode.message} fullWidth label='Fund Code' {...field} />} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Controller name="orgCode" control={control} render={({ field }) => <TextField size='small' margin='dense' disabled={disabled} error={isError(errors.orgCode)} helperText={errors.orgCode && errors.orgCode.message} fullWidth label='Org Code' {...field} />} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Controller name="objectCode" control={control} render={({ field }) => <TextField size='small' margin='dense' disabled={disabled} error={isError(errors.objectCode)} helperText={errors.objectCode && errors.objectCode.message} fullWidth label='Object Code' {...field} />} />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="gasb87Compliant"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={!!field.value}
                                            onChange={(e) => {
                                                console.log('[GASB87] Checkbox changed to:', e.target.checked)
                                                console.log('[GASB87] Current field value:', field.value)
                                                field.onChange(e.target.checked)
                                            }}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            inputRef={field.ref}
                                            disabled={disabled}
                                        />
                                    }
                                    label="GASB 87 Compliant"
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <FormControl error={isError(errors.status)} required disabled={disabled} margin='dense' fullWidth size='small'>
                                    <InputLabel shrink>Status</InputLabel>
                                    <NativeSelect {...field}>{getOptions(menu.status)}</NativeSelect>
                                    {errors.status && <FormHelperText>{errors.status.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="tenantId"
                            control={control}
                            render={({ field }) => (
                                <FormControl error={isError(errors.tenantId)} required disabled={disabled} margin='dense' fullWidth size='small'>
                                    <InputLabel shrink>Owner</InputLabel>
                                    <NativeSelect {...field}>{getOptions(menu.tenants)}</NativeSelect>
                                    {errors.tenantId && <FormHelperText>{errors.tenantId.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="startDate"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    disabled={disabled}
                                    margin='dense'
                                    label='Start Date'
                                    renderInput={(params) => <TextField size='small' helperText={errors.startDate && errors.startDate.message} fullWidth required {...params} error={isError(errors.startDate)} onChange={field.onChange} />}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    {renewable && (
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="endDate"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        disabled={disabled}
                                        margin='dense'
                                        label='End Date'
                                        renderInput={(params) => <TextField size='small' helperText={errors.endDate && errors.endDate.message} fullWidth required {...params} error={isError(errors.endDate)} onChange={field.onChange} />}
                                        {...field}
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="renewable"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    label={disabled ? 'Renewable' : 'Is Renewable'}
                                    control={
                                       <Checkbox
                                            checked={!!field.value}
                                            onChange={(evt) => field.onChange(evt.target.checked)}
                                            disabled={disabled}
                                        />
                                    }
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </FormSection>

            <FormSection title="Billing & Terms" divider>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Controller name="leaseTerms" control={control} render={({ field }) => <TextField size='small' multiline minRows={4} disabled={disabled} margin='dense' error={isError(errors.leaseTerms)} helperText={errors.leaseTerms && errors.leaseTerms.message} fullWidth label='Lease Terms' {...field} />} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="invoiceCycleId"
                            control={control}
                            render={({ field }) => (
                                <FormControl required error={isError(errors.invoiceCycleId)} disabled={disabled} margin='dense' fullWidth size='small'>
                                    <InputLabel shrink>Invoice Cycle</InputLabel>
                                    <NativeSelect {...field}>{getOptions(menu.invoice)}</NativeSelect>
                                    {errors.invoiceCycleId && <FormHelperText>{errors.invoiceCycleId.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="rentAdjustmentType"
                            control={control}
                            render={({ field }) => (
                                <FormControl required error={isError(errors.rentAdjustmentType)} disabled={disabled} margin='dense' fullWidth size='small'>
                                    <InputLabel shrink>Rent Adjustment Type</InputLabel>
                                    <NativeSelect {...field}>{getOptions(menu.payment)}</NativeSelect>
                                    {errors.rentAdjustmentType && <FormHelperText>{errors.rentAdjustmentType.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="adjustmentDate"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    disabled={disabled}
                                    margin='dense'
                                    label='Adjustment Start Date'
                                    renderInput={(params) => <TextField size='small' helperText={errors.adjustmentDate && errors.adjustmentDate.message} fullWidth {...params} error={isError(errors.adjustmentDate)} onChange={field.onChange} />}
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="adjustmentFreq" control={control} render={({ field }) => <TextField size='small' type='number' disabled={disabled} margin='dense' error={isError(errors.adjustmentFreq)} helperText={errors.adjustmentFreq && errors.adjustmentFreq.message} fullWidth label='Adjustment Freq.' {...field} />} />
                    </Grid>
                </Grid>
            </FormSection>
        </>
    )
}

function AddAgreementView() {
    const navigate = useNavigate()
    const [menu, setMenu] = useState({ invoice: [], payment: [], status: ['Active', 'Archived', 'Expiring Soon', 'Expired'], tenants: [] })
    const [requireEnddate, setRequireEnddate] = useState(false)
    
    const methods = useForm({
        resolver: yupResolver(requireEnddate ? agreementSchema : agrrementSchemaNoEnd),
        defaultValues: {
            title: '',
            agreementNum: '',
            description: '',
            startDate: '',
            endDate: '',
            status: '',
            tenantId: '',
            renewable: false,
            leaseTerms: '',
            invoiceCycleId: '',
            rentAdjustmentType: '',
            adjustmentDate: '',
            adjustmentFreq: '',
            fundCode: '',
            orgCode: '',
            objectCode: '',
            gasb87Compliant: false
        }
    });
    const { control, formState: { errors }, clearErrors, reset, getValues, setError } = methods;

    const { isDirty } = useFormState({ control })

    // Watch renewable field to update validation
    const renewable = useWatch({ control, name: 'renewable' });
    
    useEffect(() => {
        setRequireEnddate(Boolean(renewable));
    }, [renewable]);

    useEffect(() => {
        (async () => {
            let opts = await Promise.all([GetInvoiceCycles(), GetPaymentTypes(), GetActiveCompanies()])
            opts = opts.map(e => e.data)
            setMenu(x => { return { ...x, invoice: opts[0], payment: opts[1], tenants: opts[2] } })
        })()
    }, [])

    const onSubmit = data => {
        (async () => {
            const resp = await AddAgreement(data).catch((err) => {
                handleErr(err, setError)
            })
            if (resp?.data?._id) {
                navigate(`/agreements/${resp.data._id}`);
            }
        })()

    }

    return (
        <PageShell>
            <PageHeader title="Add Account" back onBack={() => navigate('/agreements')} />
            <PageContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ width: '100%' }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Form methods={methods} onSubmit={onSubmit}>
                            <AgreementFields
                                control={control}
                                errors={errors}
                                menu={menu}
                              
                            />

                            <FormActions>
                                <Button onClick={() => navigate('/agreements')}>Cancel</Button>
                                <Button variant="contained" type="submit" startIcon={<SaveIcon />}>Save</Button>
                            </FormActions>
                        </Form>
                    </LocalizationProvider>
                </Box>
            </PageContent>
        </PageShell>
    )
}

function EditAgreementView() {

    const { id } = useParams()
    const [data, setData] = useState({});
    const [refresh, setRefresh] = useState(false);
    const [menu, setMenu] = useState({ invoice: [], payment: [], status: ['Active', 'Archived', 'Expiring Soon', 'Expired'], tenants: [] })
    const [mode, setMode] = useState(0)
    const [requireEnddate, setRequireEnddate] = useState(false)
     
    const [documentsOpen, setDocumentsOpen] = useState(false)
    const [remarksOpen, setRemarksOpen] = useState(false)
    const [toolsOpen, setToolsOpen] = useState(false)
    const [sendEmailOpen, setSendEmailOpen] = useState(false)
    const navigate = useNavigate()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const methods = useForm({
        resolver: yupResolver(requireEnddate ? agreementSchema : agrrementSchemaNoEnd),
        defaultValues: {
            title: '',
            agreementNum: '',
            description: '',
            startDate: '',
            endDate: '',
            status: '',
            tenantId: '',
            renewable: false,
            leaseTerms: '',
            invoiceCycleId: '',
            rentAdjustmentType: '',
            adjustmentDate: '',
            adjustmentFreq: '',
            fundCode: '',
            orgCode: '',
            objectCode: '',
            gasb87Compliant: false
        }
    });
    const { control, formState: { errors }, clearErrors, reset, getValues, setError } = methods;

    const { isDirty } = useFormState({ control })

    // Watch renewable field to update validation
    const renewable = useWatch({ control, name: 'renewable' });
    
    useEffect(() => {
        setRequireEnddate(Boolean(renewable));
    }, [renewable]);

    useEffect(() => {
        (async () => {
            const result = await GetAgreementById(id)
            setData(result.data)
           
            reset({
                title: result.data.title ?? '',
                agreementNum: result.data.agreementNum ?? '',
                description: result.data.description ?? '',
                startDate: result.data.startDate ?? '',
                endDate: result.data.endDate ?? '',
                status: result.data.status ?? '',
                tenantId: result.data.tenantId ?? '',
                renewable: Boolean(result.data.renewable),
                leaseTerms: result.data.leaseTerms ?? '',
                invoiceCycleId: result.data.invoiceCycleId ?? '',
                rentAdjustmentType: result.data.rentAdjustmentType ?? '',
                adjustmentDate: result.data.adjustmentDate ?? '',
                adjustmentFreq: result.data.adjustmentFreq ?? '',
                fundCode: result.data.fundCode ?? '',
                orgCode: result.data.orgCode ?? '',
                objectCode: result.data.objectCode ?? '',
                gasb87Compliant: Boolean(result.data.gasb87Compliant)
            })
            clearErrors()
        })()
    }, [id, refresh])

    useEffect(() => {
        (async () => {
            let opts = await Promise.all([GetInvoiceCycles(), GetPaymentTypes(), GetActiveCompanies()])
            opts = opts.map(e => e.data)
            setMenu(x => { return { ...x, invoice: opts[0], payment: opts[1], tenants: opts[2] } })
        })()
    }, [])

    const onSubmit = data => {
        (async () => {
            const resp = await UpdateAgreement(id, data).catch((err) => {
                handleErr(err, setError)
            })

            if (resp) {
                reset(data);
                clearErrors();
            }  
        })()

    }

    const toggleRefresh = () => setRefresh(r => !r)

    const addDocument = (doc) => AddDocument(id, doc).then(toggleRefresh)
    const addRemark = (doc) => AddRemark(id, doc).then(toggleRefresh)

    const handleInvoice = (values) => {
        (async () => {
            const res = await createAgreementInvoice(id, values)
            setMode(0)
            if (res.data?._id) {
                navigate('/invoices/' + res.data._id)
            } else {
                navigate('/invoices')
            }
        })()
    }

    const handleMode = setMode
    const isReadOnly = data.status === 'Archived' || !Access.isAdmin('Lease')

    return (
        <PageShell>
            <PageHeader
                title={isReadOnly ? 'Account' : 'Edit Account'}
                back
                onBack={() => navigate(-1)}
                actions={
                    isMobile ? (
                        <Action
                            primary
                            icon={<MenuIcon />}
                            label="Agreement Tools"
                            onClick={() => setToolsOpen(true)}
                        />
                    ) : null
                }
            />
            <PageContent sx={{ p: { xs: 2, md: 3 } }}>
                <DocumentView
                    module={'Lease'}
                    docIds={data.documents}
                    onUpload={e => addDocument(e._id)}
                    open={documentsOpen}
                    onClose={() => setDocumentsOpen(false)}
                    showTrigger={false}
                />
                <RemarksView
                    module={'Lease'}
                    fullWidth
                    docIds={data.remarks}
                    onUpload={e => addRemark(e._id)}
                    open={remarksOpen}
                    onClose={() => setRemarksOpen(false)}
                    showTrigger={false}
                />
                <FormDialog open={mode !== 0} handleClose={() => handleMode(0)} handleSave={handleInvoice} />
                <SendTemplateDialog
                    open={sendEmailOpen}
                    onClose={() => setSendEmailOpen(false)}
                    module="Agreements"
                    entityId={id}
                    defaultEmail={data.email || ''}
                />

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row-reverse' },
                            gap: 2,
                            alignItems: 'flex-start',
                        }}
                    >
                        <LocalSubnav
                            title="Agreement Tools"
                            mobileLabel="Agreement Tools"
                            open={toolsOpen}
                            onOpen={() => setToolsOpen(true)}
                            onClose={() => setToolsOpen(false)}
                            showMobileTrigger={false}
                            items={[
                                {
                                    key: 'documents',
                                    label: 'Documents',
                                    icon: <FileCopyIcon />,
                                    onClick: () => setDocumentsOpen(true),
                                },
                                {
                                    key: 'remarks',
                                    label: 'Remarks',
                                    icon: <NoteIcon />,
                                    onClick: () => setRemarksOpen(true),
                                },
                                ...(!isReadOnly ? [{
                                    key: 'invoice',
                                    label: 'Create Invoice',
                                    icon: <ReceiptIcon />,
                                    onClick: () => handleMode(1),
                                    dividerBefore: true,
                                }] : []),
                                {
                                    key: 'send-email',
                                    label: 'Send Email',
                                    icon: <EmailIcon />,
                                    onClick: () => setSendEmailOpen(true),
                                    dividerBefore: isReadOnly,
                                },
                            ]}
                        />

                        <Box sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                            <Form key={id} methods={methods} onSubmit={onSubmit}>
                                <AgreementFields
                                    control={control}
                                    errors={errors}
                                    menu={menu}
                                    
                                    disabled={isReadOnly}
                                />

                                {!isReadOnly && (
                                    <FormActions>
                                        <Button onClick={() => navigate(-1)}>Cancel</Button>
                                        <Button variant="contained" type="submit" startIcon={<SaveIcon />}>Save</Button>
                                    </FormActions>
                                )}
                            </Form>

                            <FormSection title="Properties" divider sx={{ mt: 2 }}>
                                <PropertyList isEdit={!isReadOnly} agreementId={id} />
                            </FormSection>
                        </Box>
                    </Box>
                </LocalizationProvider>
            </PageContent>
        </PageShell>
    )

}

function FormDialog({ open = false, data, handleClose, handleSave }) {
    const schema = yup.object({
        startDate: yup.string().trim().required(),
        endDate: yup.string().trim().required(),
    }).required();

    const { control, handleSubmit, formState: { errors }, clearErrors, reset } = useForm({
        defaultValues: {
            startDate: '',
            endDate: ''
        },
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        clearErrors()
        reset()
    }, [open, clearErrors, reset])

    const onSubmit = handleSave

    const isError = (val) => val !== undefined && val !== null && typeof val === 'object'


    return <ResponsiveDialog open={open} onClose={handleClose}>
        <DialogTitle> Invoice </DialogTitle>
        <DialogContent>
            <DialogContentText>&nbsp;</DialogContentText>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller name="startDate" control={control} render={({ field }) => <DatePicker margin='dense' helperText={errors.startDate && errors.startDate.message} fullWidth label='Start Date' renderInput={(params) => <TextField size='small' {...params} error={isError(errors.startDate)} onChange={field.onChange} />} {...field} />} />
                <Controller name="endDate" control={control} render={({ field }) => <DatePicker margin='dense' helperText={errors.endDate && errors.endDate.message} fullWidth label='End Date' renderInput={(params) => <TextField size='small' {...params} error={isError(errors.endDate)} onChange={field.onChange} />} {...field} />} />
            </LocalizationProvider>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSubmit(onSubmit)}>Save</Button>
        </DialogActions>
    </ResponsiveDialog>
}
